<script setup lang="tsx">
import { ref } from 'vue'
import type { DocTreeNode } from '@/models/api-generated'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

interface Props {
	isOpen: boolean
	title?: string
	rootNode?: DocTreeNode
	currentPath?: string
}

interface Emits {
	(e: 'close'): void
	(e: 'select', path: string): void
}

const props = defineProps<Props>()

const emit = defineEmits<Emits>()

const selectedPath = ref<string>('')
const expandedNodes = ref<Set<string>>(new Set())

// Recursively render directory tree
const renderNode = (node: DocTreeNode, level: number = 0) => {
	const isExpanded = expandedNodes.value.has(node.path)
	const hasChildren = (node.children ?? []).length > 0
	const isSelected = selectedPath.value === node.path

	return (
		<div key={node.path}>
			<div
				class={`directory-item ${level > 0 ? 'ms-' + Math.min(level * 2, 5) : ''}`}
				style={{
					backgroundColor: isSelected ? '#0d6efd' : 'transparent',
					color: isSelected ? 'white' : 'inherit',
				}}
				onClick={() => {
					selectedPath.value = node.path
				}}
			>
				{hasChildren && (
					<button
						class="btn btn-sm btn-link p-0 me-1"
						onClick={(e) => {
							e.stopPropagation()
							if (isExpanded) {
								expandedNodes.value.delete(node.path)
							} else {
								expandedNodes.value.add(node.path)
							}
						}}
						style={{ width: '24px', color: isSelected ? 'white' : 'inherit' }}
					>
						<i class={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
					</button>
				)}
				{!hasChildren && <span style={{ width: '24px', display: 'inline-block' }}></span>}
				<i class="bi bi-folder-fill me-2" style={{ color: isSelected ? 'white' : '#6c757d' }}></i>
				<span style={{ color: isSelected ? 'white' : 'inherit' }}>{node.name || `${t('document.label', 2)}`}</span>
			</div>

			{isExpanded && hasChildren && <div>{(node.children ?? []).map((child) => renderNode(child, level + 1))}</div>}
		</div>
	)
}

const handleSelect = () => {
	if (selectedPath.value.trim()) {
		emit('select', selectedPath.value)
		emit('close')
	}
}

const handleCancel = () => {
	selectedPath.value = ''
	expandedNodes.value.clear()
	emit('close')
}
</script>
<template>
	<div v-if="isOpen" class="modal-backdrop">
		<div class="modal modal-content p-3" style="max-height: 80vh; overflow-y: auto">
			<h5 class="mb-3">{{ title ?? $t('document.selectNewDirectory') }}</h5>
			<div v-if="rootNode" class="directory-tree border rounded p-2 mb-3" style="max-height: 50vh; overflow-y: auto">
				<component :is="() => rootNode && renderNode(rootNode)" />
			</div>
			<div class="d-flex justify-content-end gap-2">
				<button class="btn btn-secondary" @click="handleCancel">{{ $t('misc.cancel') }}</button>
				<button class="btn btn-primary" :disabled="!selectedPath" @click="handleSelect">{{ $t('misc.select') }}</button>
			</div>
		</div>
	</div>
</template>

<style lang="sass" scoped>
.modal-backdrop
	position: fixed
	inset: 0
	background: gray
	display: flex
	align-items: center
	justify-content: center
	z-index: 1050

.modal-content
	border-radius: .5rem
	width: min(500px, calc(100% - 2rem))
	height: auto


.directory-tree
	border-color: #dee2e6

.directory-item
	padding: 0.5rem
	cursor: pointer
	display: flex
	align-items: center
	user-select: none
	color: inherit
</style>
